/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — auth-team.ts
   Team management for firm/firm+ plans.
   Phase 1: role-based access control (owner / admin / member).
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import { FIREBASE_ENABLED, getFirebaseUid } from './config';
import { esc, el, elInput, showToast } from './utils';
import {
  type FirmData,
  type FirmRole,
  normalizeFirmData,
  canManageTeam,
  canChangeRole,
  canRemoveMember,
  getRoleForEmail,
  isFirmOwner,
} from './auth-firm-roles';

declare const firebase: any;

// ─── Cloud Function call helper ────────────────────────────────────────────

interface ManageTeamPayload {
  action: 'add' | 'remove' | 'changeRole';
  email: string;
  name?: string;
  newRole?: FirmRole;
}

async function callManageTeamMember(payload: ManageTeamPayload): Promise<{ success: boolean; error?: string }> {
  if (!FIREBASE_ENABLED || typeof firebase === 'undefined') {
    return { success: false, error: 'Firebase not enabled.' };
  }
  try {
    const callable = firebase.functions().httpsCallable('manageTeamMember');
    await callable(payload);
    return { success: true };
  } catch (err: any) {
    const msg = err?.message || 'Unknown error';
    return { success: false, error: msg };
  }
}

/** Queue an invitation email to a newly-added team member. Phase 3. */
async function queueInvitationEmail(toEmail: string, toName: string): Promise<void> {
  if (!FIREBASE_ENABLED || !state.firebaseDb || !state.currentUserEmail) return;
  try {
    await state.firebaseDb.collection('emailQueue').add({
      to: toEmail,
      subject: `You've been invited to NoteFlow by ${state.currentUserEmail}`,
      eventType: 'team-invite',
      from: state.currentUserEmail,
      inviterEmail: state.currentUserEmail,
      inviteeName: toName || '',
      createdAt: new Date().toISOString(),
      sent: false,
    });
  } catch (err) {
    console.warn('Failed to queue invitation email:', err);
    // Non-fatal — member is still added to firm
  }
}

// ─── Firestore helpers ─────────────────────────────────────────────────────

function loadFirmData(callback: (firm: FirmData | null) => void): void {
  const uid: string | null = getFirebaseUid();
  if (!uid) { callback(null); return; }
  state.firebaseDb.collection('users').doc(uid).get()
    .then(function(doc: any) {
      if (!doc.exists) { callback(null); return; }
      const data = doc.data();
      const ownerEmail = data.email || state.currentUserEmail || '';
      // Always run normalization (handles legacy data with no roles)
      const firm = normalizeFirmData(data.firm || null, ownerEmail);
      callback(firm);
    })
    .catch(function() { callback(null); });
}

function saveFirmData(firmData: FirmData): Promise<void> {
  const uid: string | null = getFirebaseUid();
  if (!uid) return Promise.resolve();
  return state.firebaseDb.collection('users').doc(uid).set({ firm: firmData }, { merge: true })
    .catch(function(err: any) {
      console.error('Failed to save firm data:', err);
      showToast('Failed to save team changes', 'error');
    });
}

// ─── UI ────────────────────────────────────────────────────────────────────

export function toggleTeamPanel(): void {
  const panel = el('team-panel');
  if (!panel) return;
  panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
  if (panel.style.display === 'block') renderTeamMembers();
}

function roleLabel(role: FirmRole): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function roleBadge(role: FirmRole): string {
  const colors: Record<FirmRole, string> = {
    owner: 'background:#7c3aed;color:#fff',
    admin: 'background:#2563eb;color:#fff',
    member: 'background:#e5e7eb;color:#374151',
  };
  return '<span style="' + colors[role] + ';padding:2px 8px;border-radius:4px;font-size:0.7rem;font-weight:600;text-transform:uppercase">' + esc(roleLabel(role)) + '</span>';
}

function statusBadge(status?: 'pending' | 'active'): string {
  if (status === 'pending') {
    return '<span style="background:#fef3c7;color:#92400e;padding:1px 6px;border-radius:4px;font-size:0.65rem;font-weight:600;margin-left:6px">PENDING</span>';
  }
  return '';
}

function renderTeamMembers(): void {
  loadFirmData(function(firm) {
    if (!firm) return;
    const myEmail = state.currentUserEmail || '';
    const myRole = getRoleForEmail(firm, myEmail);
    const canManage = canManageTeam(firm, myEmail);

    interface DisplayRow {
      email: string;
      name?: string;
      role: FirmRole;
      status?: 'pending' | 'active';
      addedAt?: string;
      isOwnerRow: boolean;
      memberIndex: number; // -1 for owner
    }

    const rowsList: DisplayRow[] = [];
    if (firm.ownerEmail) {
      rowsList.push({
        email: firm.ownerEmail,
        name: firm.ownerEmail.toLowerCase() === myEmail.toLowerCase() ? (state.currentUserName || '') : '',
        role: 'owner',
        status: 'active',
        isOwnerRow: true,
        memberIndex: -1,
      });
    }
    firm.members.forEach(function(m, i) {
      rowsList.push({
        email: m.email,
        name: m.name,
        role: m.role,
        status: m.status,
        addedAt: m.addedAt,
        isOwnerRow: false,
        memberIndex: i,
      });
    });

    // Header / seat count
    const seatCountEl = document.getElementById('team-seat-count');
    if (seatCountEl) {
      seatCountEl.textContent = firm.members.length + ' / ' + firm.maxSeats + ' seats used';
    }

    // Show/hide the "add member" form based on permissions
    const addForm = document.getElementById('team-add-form');
    if (addForm) addForm.style.display = canManage ? 'block' : 'none';

    let html: string;
    if (rowsList.length === 0) {
      html = '<p style="color:var(--muted);font-size:0.85rem">No team members. Add members by email to give them access.</p>';
    } else {
      html = '<table style="width:100%;font-size:0.85rem;border-collapse:collapse">';
      html += '<tr style="border-bottom:1px solid var(--border);color:var(--muted);font-size:0.75rem">' +
        '<td style="padding:6px 0">Email</td>' +
        '<td>Name</td>' +
        '<td>Role</td>' +
        '<td>Added</td>' +
        '<td></td>' +
        '</tr>';

      rowsList.forEach(function(row) {
        const dateStr: string = row.addedAt
          ? new Date(row.addedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : '';
        const isMe = row.email.toLowerCase() === myEmail.toLowerCase();

        html += '<tr style="border-bottom:1px solid var(--border)">';
        html += '<td style="padding:8px 0">' + esc(row.email) +
          (isMe ? ' <span style="color:var(--muted);font-size:0.75rem">(you)</span>' : '') +
          statusBadge(row.status) +
          '</td>';
        html += '<td>' + esc(row.name || '-') + '</td>';

        // Role cell — dropdown if user has permission to change this role
        html += '<td>';
        if (
          !row.isOwnerRow &&
          canManage &&
          canChangeRole(firm, myEmail, row.email, row.role).allowed
        ) {
          html += '<select data-role-select data-email="' + esc(row.email) + '" style="font-size:0.8rem;padding:2px 6px">';
          (['admin', 'member'] as FirmRole[]).forEach(function(r) {
            const sel = r === row.role ? ' selected' : '';
            const allowed = canChangeRole(firm, myEmail, row.email, r).allowed;
            if (!allowed && r !== row.role) return;
            html += '<option value="' + r + '"' + sel + '>' + roleLabel(r) + '</option>';
          });
          html += '</select>';
        } else {
          html += roleBadge(row.role);
        }
        html += '</td>';

        html += '<td style="color:var(--muted)">' + esc(dateStr) + '</td>';

        // Remove button — show only if action is permitted
        html += '<td style="text-align:right">';
        const removeCheck = canRemoveMember(firm, myEmail, row.email);
        if (!row.isOwnerRow && removeCheck.allowed) {
          html += '<button class="btn btn-ghost btn-sm" data-action="removeTeamMember" data-param="' + row.memberIndex + '" style="color:var(--red);font-size:0.7rem">Remove</button>';
        }
        html += '</td>';

        html += '</tr>';
      });
      html += '</table>';
    }

    // "your role" indicator at the top
    const myRoleLine = myRole
      ? '<div style="font-size:0.78rem;color:var(--muted);margin-bottom:8px">Your role: ' + roleBadge(myRole) + '</div>'
      : '';

    const listEl = document.getElementById('team-member-list');
    if (listEl) {
      listEl.innerHTML = myRoleLine + html;
      // Attach change listeners to role dropdowns (event delegation alternative)
      listEl.querySelectorAll<HTMLSelectElement>('select[data-role-select]').forEach(function(sel) {
        sel.addEventListener('change', function() {
          const email = sel.getAttribute('data-email') || '';
          changeRole(email, sel.value as FirmRole);
        });
      });
    }
  });
}

// ─── Actions ──────────────────────────────────────────────────────────────

export async function addTeamMember(): Promise<void> {
  const emailInput = elInput('team-add-email');
  const nameInput = elInput('team-add-name');
  if (!emailInput || !nameInput) return;
  const email: string = emailInput.value.trim().toLowerCase();
  const name: string = nameInput.value.trim();

  if (!email || !email.includes('@')) {
    alert('Please enter a valid email address.');
    return;
  }

  // Pre-check client-side for fast feedback (server enforces too)
  const firm = await new Promise<FirmData | null>(r => loadFirmData(r));
  if (!firm) { alert('No firm data found. Upgrade to a firm plan first.'); return; }
  if (!canManageTeam(firm, state.currentUserEmail)) {
    alert('Only firm owners and admins can add team members.');
    return;
  }
  if (firm.members.length >= firm.maxSeats) {
    alert('Team is at capacity (' + firm.maxSeats + ' seats). Contact support to add more seats.');
    return;
  }
  if (firm.ownerEmail && firm.ownerEmail.toLowerCase() === email) {
    alert('This email is already the firm owner.');
    return;
  }
  if (firm.members.some(m => m.email.toLowerCase() === email)) {
    alert('This email is already on the team.');
    return;
  }

  // If the actor is the owner, they can write directly (faster). Otherwise, use Cloud Function.
  if (isFirmOwner(firm, state.currentUserEmail)) {
    firm.members.push({
      email, name,
      role: 'member',
      status: 'pending', // Phase 3: pending until first sign-in
      addedAt: new Date().toISOString(),
      addedBy: state.currentUserEmail || undefined,
    });
    firm.memberEmails = firm.members.map(m => m.email);
    await saveFirmData(firm);
    // Phase 3: queue invitation email
    await queueInvitationEmail(email, name);
  } else {
    // Admin path: call Cloud Function (uses Admin SDK to bypass owner-only write rule).
    // The function also queues the invitation email server-side.
    const result = await callManageTeamMember({ action: 'add', email, name });
    if (!result.success) { alert(result.error || 'Failed to add member.'); return; }
  }

  emailInput.value = '';
  nameInput.value = '';
  showToast('Added ' + email + ' to the team', 'success');
  renderTeamMembers();
}

export async function removeTeamMember(indexStr: string | number): Promise<void> {
  const index: number = typeof indexStr === 'string' ? parseInt(indexStr, 10) : indexStr;
  if (isNaN(index) || index < 0) return;

  const firm = await new Promise<FirmData | null>(r => loadFirmData(r));
  if (!firm || !firm.members[index]) return;
  const member = firm.members[index];

  const check = canRemoveMember(firm, state.currentUserEmail, member.email);
  if (!check.allowed) { alert(check.reason || 'Permission denied.'); return; }

  if (!confirm('Remove ' + member.email + ' from the team?')) return;

  if (isFirmOwner(firm, state.currentUserEmail)) {
    firm.members.splice(index, 1);
    firm.memberEmails = firm.members.map(m => m.email);
    await saveFirmData(firm);
  } else {
    const result = await callManageTeamMember({ action: 'remove', email: member.email });
    if (!result.success) { alert(result.error || 'Failed to remove member.'); return; }
  }

  showToast('Removed ' + member.email, 'info');
  renderTeamMembers();
}

/** Change a team member's role. Called from the role dropdown change event. */
export async function changeRole(targetEmail: string, newRole: FirmRole): Promise<void> {
  const firm = await new Promise<FirmData | null>(r => loadFirmData(r));
  if (!firm) return;

  const check = canChangeRole(firm, state.currentUserEmail, targetEmail, newRole);
  if (!check.allowed) {
    alert(check.reason || 'Permission denied.');
    renderTeamMembers();
    return;
  }

  if (isFirmOwner(firm, state.currentUserEmail)) {
    const idx = firm.members.findIndex(m => m.email.toLowerCase() === targetEmail.toLowerCase());
    if (idx < 0) return;
    firm.members[idx].role = newRole;
    await saveFirmData(firm);
  } else {
    const result = await callManageTeamMember({ action: 'changeRole', email: targetEmail, newRole });
    if (!result.success) { alert(result.error || 'Failed to change role.'); renderTeamMembers(); return; }
  }

  showToast('Updated role for ' + targetEmail + ' to ' + newRole, 'success');
  renderTeamMembers();
}
